// src/pages/ordens-servico/EditarOS.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import LayoutDesktop from "../../layouts/LayoutDesktop";

import { db } from "../../config/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

function renderCodigoOs(os) {
  if (os.codigo) return os.codigo;
  if (os.codigoSequencial != null) {
    return `OS${String(os.codigoSequencial).padStart(4, "0")}`;
  }
  return "-";
}

function formatTimestampBR(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : null);
    if (!d || Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("pt-BR");
  } catch {
    return "";
  }
}

function EditarOS() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const { osId } = useParams();

  const [osOriginal, setOsOriginal] = useState(null);
  const [carregandoOs, setCarregandoOs] = useState(true);

  const [materiaisOs, setMateriaisOs] = useState([]);
  const [carregandoMateriaisOs, setCarregandoMateriaisOs] = useState(false);

  const [empresas, setEmpresas] = useState([]);
  const [setores, setSetores] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [statusList, setStatusList] = useState([]);

  const [carregandoEmpresas, setCarregandoEmpresas] = useState(false);
  const [carregandoSetores, setCarregandoSetores] = useState(false);
  const [carregandoMaquinas, setCarregandoMaquinas] = useState(false);
  const [carregandoStatus, setCarregandoStatus] = useState(false);

  const [empresaId, setEmpresaId] = useState("");
  const [setorId, setSetorId] = useState("");
  const [maquinaId, setMaquinaId] = useState("");
  const [statusId, setStatusId] = useState("");

  const [titulo, setTitulo] = useState("");
  const [defeitos, setDefeitos] = useState([]);
  const [carregandoDefeitos, setCarregandoDefeitos] = useState(false);
  const [defeitoId, setDefeitoId] = useState("");
  const [defeitoCustom, setDefeitoCustom] = useState("");
  const [descricao, setDescricao] = useState("");
  const [criticidade, setCriticidade] = useState("Média");
  const [maquinaParou, setMaquinaParou] = useState("Sim");
  const [tipoServico, setTipoServico] = useState("Corretiva");

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [salvando, setSalvando] = useState(false);

  const materiaisAgrupados = useMemo(() => {
    const map = new Map();
    (materiaisOs || []).forEach((m) => {
      const produto = String(m.produto || "-");
      const codigo = m.codigo ? String(m.codigo) : "";
      const local = m.local ? String(m.local) : "";
      const chave = `${produto}||${codigo}||${local}`;
      const atual = map.get(chave) || { produto, codigo, local, quantidade: 0 };
      atual.quantidade += Number(m.quantidade) || 0;
      map.set(chave, atual);
    });
    return Array.from(map.values()).sort((a, b) => a.produto.localeCompare(b.produto, "pt-BR", { sensitivity: "base" }));
  }, [materiaisOs]);

  const totalMateriais = useMemo(() => {
    return materiaisAgrupados.reduce((acc, x) => acc + (Number(x.quantidade) || 0), 0);
  }, [materiaisAgrupados]);

  useEffect(() => {
    async function carregarOS() {
      if (!osId) return;

      setCarregandoOs(true);
      try {
        const osRef = doc(db, "ordensServico", osId);
        const osSnap = await getDoc(osRef);
        if (osSnap.exists()) {
          const data = { id: osSnap.id, ...osSnap.data() };
          setOsOriginal(data);
          
          setEmpresaId(data.empresaId || "");
          setSetorId(data.setorId || "");
          setMaquinaId(data.maquinaId || "");
          setStatusId(data.statusId || "");
          setTitulo(data.titulo || data.defeitoNome || "");
          setDescricao(data.descricao || "");
          setCriticidade(data.criticidade || "Média");
          setMaquinaParou(data.maquinaParou || "Sim");
          setTipoServico(data.tipoServico || "Corretiva");
          
          if (data.defeitoCustom) {
            setDefeitoId("__OUTROS__");
            setDefeitoCustom(data.titulo || data.defeitoNome || "");
          } else if (data.defeitoId) {
            setDefeitoId(data.defeitoId);
          }
        } else {
          setErro("OS não encontrada.");
        }
      } catch (e) {
        console.error("Erro ao carregar OS:", e);
        setErro("Erro ao carregar OS.");
      } finally {
        setCarregandoOs(false);
      }
    }

    carregarOS();
}, [osId]);

useEffect(() => {
  async function carregarMateriaisDaOs() {
    if (!osId) return;
    if (!usuarioLogado?.grupoId) return;

    setCarregandoMateriaisOs(true);
    try {
      const ref = collection(db, "osMateriais");
      // Para evitar índice composto, filtramos pelo osId e validamos grupo no client.
      const q = query(ref, where("osId", "==", osId));
      const snap = await getDocs(q);

      const lista = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((x) => x.grupoId === usuarioLogado.grupoId);

      setMateriaisOs(lista);
    } catch (e) {
      console.warn("Erro ao carregar materiais da OS:", e);
      setMateriaisOs([]);
    } finally {
      setCarregandoMateriaisOs(false);
    }
  }

  carregarMateriaisDaOs();
}, [osId, usuarioLogado?.grupoId]);

useEffect(() => {
  async function carregarBase() {
      if (!usuarioLogado?.grupoId) return;

      const grupoId = usuarioLogado.grupoId;

      setCarregandoEmpresas(true);
      try {
        const empresasRef = collection(db, "empresas");
        const qEmp = query(empresasRef, where("grupoId", "==", grupoId));
        const snapEmp = await getDocs(qEmp);
        const listaEmp = snapEmp.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setEmpresas(listaEmp);
      } catch (e) {
        console.error("Erro ao carregar empresas:", e);
      } finally {
        setCarregandoEmpresas(false);
      }

      setCarregandoSetores(true);
      try {
        const setoresRef = collection(db, "setores");
        const qSet = query(setoresRef, where("grupoId", "==", grupoId));
        const snapSet = await getDocs(qSet);
        const listaSet = snapSet.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setSetores(listaSet);
      } catch (e) {
        console.error("Erro ao carregar setores:", e);
      } finally {
        setCarregandoSetores(false);
      }

      setCarregandoMaquinas(true);
      try {
        const maqRef = collection(db, "maquinas");
        const qMaq = query(maqRef, where("grupoId", "==", grupoId));
        const snapMaq = await getDocs(qMaq);
        const listaMaq = snapMaq.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setMaquinas(listaMaq);
      } catch (e) {
        console.error("Erro ao carregar máquinas:", e);
      } finally {
        setCarregandoMaquinas(false);
      }

      setCarregandoStatus(true);
      try {
        const statusRef = collection(db, "statusOs");
        const qStatus = query(statusRef, where("grupoId", "==", grupoId));
        const snapStatus = await getDocs(qStatus);
        const listaStatusDb = snapStatus.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        const statusFabrica = [
          {id:"fab_aberta", nome:"Aberta", cor:"#2563eb", padrao:true},
          {id:"fab_aberta_24h", nome:"Aberta 24H", cor:"#7c3aed", padrao:true},
          {id:"fab_em_atendimento", nome:"Em Atendimento", cor:"#0ea5e9", padrao:true},
          {id:"fab_finalizada", nome:"Finalizada", cor:"#16a34a", padrao:true},
          {id:"fab_cancelada", nome:"Cancelada", cor:"#dc2626", padrao:true},
          {id:"fab_aguard_mat", nome:"Aguardando Material", cor:"#ca8a04", padrao:true},
          {id:"fab_solic_mat", nome:"Solicitação de Material", cor:"#d97706", padrao:true},
          {id:"fab_aguard_parada", nome:"Aguardando Parada de Máquina", cor:"#64748b", padrao:true}
        ];

        const listaStatus = [...statusFabrica, ...listaStatusDb];
        setStatusList(listaStatus);
      } catch (e) {
        console.error("Erro ao carregar status:", e);
      } finally {
        setCarregandoStatus(false);
      }
    }

    carregarBase();
  }, [usuarioLogado]);

  useEffect(() => {
    async function carregarDefeitosDaMaquina() {
      setDefeitos([]);
      if (!usuarioLogado?.grupoId || !maquinaId) return;

      try {
        setCarregandoDefeitos(true);
        const defeitosRef = collection(db, "defeitos");
        const qDef = query(
          defeitosRef,
          where("grupoId", "==", usuarioLogado.grupoId),
          where("maquinasIds", "array-contains", maquinaId)
        );
        const snapDef = await getDocs(qDef);
        const listaDef = snapDef.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setDefeitos(listaDef);
      } catch (e) {
        console.error("Erro ao carregar defeitos da máquina:", e);
      } finally {
        setCarregandoDefeitos(false);
      }
    }

    carregarDefeitosDaMaquina();
  }, [usuarioLogado, maquinaId]);

  const setoresFiltrados = useMemo(() => {
    if (!empresaId) return setores;
    return setores.filter((s) => s.empresaId === empresaId);
  }, [setores, empresaId]);

  const maquinasFiltradas = useMemo(() => {
    if (!setorId) return maquinas;
    return maquinas.filter((m) => m.setorId === setorId);
  }, [maquinas, setorId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuarioLogado?.grupoId) {
      setErro("Usuário logado não possui grupo definido.");
      return;
    }

    if (!empresaId) {
      setErro("Selecione a empresa.");
      return;
    }
    if (!setorId) {
      setErro("Selecione o setor.");
      return;
    }
    if (!maquinaId) {
      setErro("Selecione a máquina.");
      return;
    }

    if (!defeitoId) {
      setErro("Selecione o defeito da OS.");
      return;
    }

    if (defeitoId === "__OUTROS__" && !defeitoCustom.trim()) {
      setErro("Descreva o defeito no campo 'Outro defeito'.");
      return;
    }

    const empresaSelecionada = empresas.find((e) => e.id === empresaId);
    const empresaNome = empresaSelecionada?.nome || "(sem nome)";

    const setorSelecionado = setores.find((s) => s.id === setorId);
    const setorNome = setorSelecionado?.nome || "(sem nome)";

    const maquinaSelecionada = maquinas.find((m) => m.id === maquinaId);
    const maquinaNome = maquinaSelecionada?.nome || "(sem nome)";
    const maquinaChave = !!maquinaSelecionada?.maquinaChave;


    let statusSelecionado = statusList.find((s) => s.id === statusId);
    if (!statusSelecionado && statusList.length > 0) {
      statusSelecionado = statusList[0];
    }

    const isDefeitoCustom = defeitoId === "__OUTROS__";
    const defeitoSelecionado = isDefeitoCustom ? null : defeitos.find((d) => d.id === defeitoId) || null;
    const tituloOs = isDefeitoCustom ? defeitoCustom.trim() : (defeitoSelecionado?.nome || titulo || "Defeito não especificado");

    setSalvando(true);

    try {
      const osRef = doc(db, "ordensServico", osId);
      const parouSim = String(maquinaParou || "Sim").toLowerCase().trim() === "sim";
      const paradaAtivaOriginal = (typeof osOriginal?.maquinaParadaAtiva === "boolean") ? osOriginal.maquinaParadaAtiva : parouSim;

      const osUpdate = {
        titulo: tituloOs,
        defeitoId: isDefeitoCustom ? null : (defeitoSelecionado ? defeitoSelecionado.id : defeitoId || null),
        defeitoNome: tituloOs,
        defeitoCustom: isDefeitoCustom,
        descricao: descricao.trim() || null,
        criticidade: criticidade || "Média",
        maquinaParou: maquinaParou || "Sim",
        // ✅ Controle de parada (estado atual): não forçar 'ativa' só porque a OS teve parada no início
        // - Se já existe um estado salvo (maquinaParadaAtiva), preserva.
        // - Se o usuário marcou 'Máquina parou = Não', encerra a parada.
        maquinaParadaAtiva: parouSim ? paradaAtivaOriginal : false,
        // Só cria "maquinaParouEm" se ainda não existir, para não perder histórico
        ...(parouSim && !osOriginal?.maquinaParouEm ? { maquinaParouEm: serverTimestamp() } : {}),
        // Se marcar "Não" aqui, encerra a parada (mesmo mantendo OS aberta)
        ...(!parouSim ? { maquinaVoltouRodarEm: serverTimestamp(), maquinaParadaEncerradaEm: serverTimestamp() } : {}),
        // Se for parada ativa de novo (em casos de correção manual), limpa o fim
        ...(parouSim && paradaAtivaOriginal ? { maquinaVoltouRodarEm: null, maquinaParadaEncerradaEm: null } : {}),
        tipoServico: tipoServico || "Corretiva",

        empresaId,
        empresaNome,
        setorId,
        setorNome,
        maquinaId,
        maquinaNome,
        maquinaChave,

        statusId: statusSelecionado?.id || null,
        statusNome: statusSelecionado?.nome || null,
        statusCor: statusSelecionado?.cor || null,

        atualizadoPor: usuarioLogado?.uid || null,
        atualizadoPorNome: usuarioLogado?.nome || null,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(osRef, osUpdate);

      setSucesso("OS atualizada com sucesso!");
      
      setTimeout(() => {
        navigate("/gerenciar/tabela-os");
      }, 1500);
    } catch (err) {
      console.error(err);
      setErro("Erro ao atualizar OS. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  };

  if (carregandoOs) {
    return (
      <LayoutDesktop
        tema={tema}
        titulo="Editar OS"
        subtitulo="Ordens de Serviço / Editar"
      >
        <section style={{ width: "100%", padding: 40, textAlign: "center" }}>
          <p>Carregando OS...</p>
        </section>
      </LayoutDesktop>
    );
  }

  if (!osOriginal) {
    return (
      <LayoutDesktop
        tema={tema}
        titulo="Editar OS"
        subtitulo="Ordens de Serviço / Editar"
      >
        <section style={{ width: "100%", padding: 40, textAlign: "center" }}>
          <p style={{ color: "#ef4444" }}>OS não encontrada.</p>
          <button
            type="button"
            className="pc-btn-secundario"
            onClick={() => navigate("/gerenciar/tabela-os")}
            style={{ marginTop: 16 }}
          >
            Voltar para Tabela de OS
          </button>
        </section>
      </LayoutDesktop>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo={`Editar ${renderCodigoOs(osOriginal)}`}
      subtitulo="Ordens de Serviço / Editar OS"
    >
      <section style={{ width: "100%" }}>
        <div
          className="pc-card"
          style={{
            width: "100%",
            paddingLeft: 40,
            paddingRight: 40,
            boxSizing: "border-box",
          }}
        >
          <header className="pc-table-header">
            <div className="pc-table-header-text">
              <h3>Editar Ordem de Serviço - {renderCodigoOs(osOriginal)}</h3>
              <p>
                Atualize os dados da OS conforme necessário.
              </p>
            </div>
          </header>

          {erro && (
            <div
              style={{
                marginTop: 12,
                padding: 8,
                borderRadius: 8,
                background: "#7f1d1d",
                color: "#fecaca",
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {sucesso && (
            <div
              style={{
                marginTop: 12,
                padding: 8,
                borderRadius: 8,
                background: "#14532d",
                color: "#bbf7d0",
                fontSize: 14,
              }}
            >
              {sucesso}
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            style={{
              marginTop: 12,
              maxWidth: 720,
              marginLeft: "auto",
              marginRight: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 14 }}>Status</span>
              <select
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
                disabled={carregandoStatus}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                <option value="">Selecione o status</option>
                {statusList.map((st) => (
                  <option key={st.id} value={st.id}>
                    {st.nome || "(sem nome)"}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span style={{ fontSize: 14 }}>Empresa</span>
              <select
                value={empresaId}
                onChange={(e) => {
                  setEmpresaId(e.target.value);
                  setSetorId("");
                  setMaquinaId("");
                }}
                disabled={carregandoEmpresas}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                <option value="">
                  {carregandoEmpresas ? "Carregando empresas..." : "Selecione a empresa"}
                </option>
                {empresas.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.nome || "(sem nome)"}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span style={{ fontSize: 14 }}>Setor</span>
              <select
                value={setorId}
                onChange={(e) => {
                  setSetorId(e.target.value);
                  setMaquinaId("");
                }}
                disabled={carregandoSetores || !empresaId}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                <option value="">
                  {carregandoSetores
                    ? "Carregando setores..."
                    : empresaId
                    ? "Selecione o setor"
                    : "Selecione a empresa primeiro"}
                </option>
                {setoresFiltrados.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome || "(sem nome)"}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span style={{ fontSize: 14 }}>Máquina</span>
              <select
                value={maquinaId}
                onChange={(e) => {
                  setMaquinaId(e.target.value);
                }}
                disabled={carregandoMaquinas || !setorId}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                <option value="">
                  {carregandoMaquinas
                    ? "Carregando máquinas..."
                    : setorId
                    ? "Selecione a máquina"
                    : "Selecione o setor primeiro"}
                </option>
                {maquinasFiltradas.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nome || "(sem nome)"}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 14 }}>Defeito</span>
              <select
                value={defeitoId}
                onChange={(e) => {
                  setDefeitoId(e.target.value);
                  if (e.target.value !== "__OUTROS__") {
                    setDefeitoCustom("");
                  }
                }}
                disabled={!maquinaId || carregandoDefeitos}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                {!maquinaId ? (
                  <option value="">Selecione a máquina primeiro</option>
                ) : carregandoDefeitos ? (
                  <option value="">Carregando defeitos...</option>
                ) : (
                  <>
                    <option value="">Selecione o defeito</option>
                    {defeitos.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.nome || "(sem nome)"}
                      </option>
                    ))}
                    <option value="__OUTROS__">Outros (descrever manualmente)</option>
                  </>
                )}
              </select>
            </label>

            {defeitoId === "__OUTROS__" && (
              <label>
                <span style={{ fontSize: 14 }}>Descreva o defeito</span>
                <input
                  type="text"
                  value={defeitoCustom}
                  onChange={(e) => setDefeitoCustom(e.target.value)}
                  placeholder="Ex: Barulho estranho no motor"
                  style={{
                    width: "100%",
                    padding: "9px 11px",
                                        borderRadius: 8,
                    border: "1px solid var(--cor-borda-input, #334155)",
                    background: "var(--cor-input-bg, transparent)",
                    color: "var(--cor-input-texto, inherit)",
                  }}
                />
              </label>
            )}

            <label>
              <span style={{ fontSize: 14 }}>Criticidade</span>
              <select
                value={criticidade}
                onChange={(e) => setCriticidade(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                <option value="Alta">Alta</option>
                <option value="Média">Média</option>
                <option value="Baixa">Baixa</option>
              </select>
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 14 }}>Tipo de Serviço</span>
              <select
                value={tipoServico}
                onChange={(e) => setTipoServico(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                <option value="Corretiva">Corretiva</option>
                <option value="Melhoria">Melhoria</option>
                <option value="Instalação">Instalação</option>
                <option value="Preventiva">Preventiva</option>
              </select>
            </label>

            <label>
              <span style={{ fontSize: 14 }}>Máquina parou por causa do defeito?</span>
              <select
                value={maquinaParou}
                onChange={(e) => setMaquinaParou(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                <option value="Sim">Sim</option>
                <option value="Não">Não</option>
              </select>
            </label>

            <label>
              <span style={{ fontSize: 14 }}>Descrição / Observações</span>
              <textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={4}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                  resize: "vertical",
                }}
              />
            </label>

            {osOriginal?.checklistUltimaExecucao ? (
              <div
                style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 14,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "rgba(15,23,42,0.35)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div style={{ fontWeight: 800 }}>Checklist (última execução)</div>
                  <div style={{ fontSize: 12, opacity: 0.75 }}>
                    {formatTimestampBR(osOriginal.checklistUltimaExecucao.executadoEm) || "—"}
                  </div>
                </div>

                <div style={{ marginTop: 8, fontSize: 13, opacity: 0.85 }}>
                  <strong>Modelo:</strong> {osOriginal.checklistUltimaExecucao.modeloNome || osOriginal.checklistModeloNome || "—"}
                  {" • "}
                  <strong>Tipo:</strong> {osOriginal.checklistUltimaExecucao.tipo || osOriginal.checklistTipo || "—"}
                </div>

                {Array.isArray(osOriginal.checklistUltimaExecucao.respostas) &&
                osOriginal.checklistUltimaExecucao.respostas.length > 0 ? (
                  <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                    {osOriginal.checklistUltimaExecucao.respostas.map((r, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: 10,
                          borderRadius: 12,
                          border: "1px solid rgba(148,163,184,0.18)",
                          background: "rgba(15,23,42,0.25)",
                        }}
                      >
                        <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 4 }}>
                          {r?.pergunta || `Item ${idx + 1}`}
                        </div>
                        <div style={{ fontSize: 13, opacity: 0.9 }}>
                          <strong>Resposta:</strong> {String(r?.valor ?? "").trim() || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ marginTop: 8, fontSize: 13, opacity: 0.75 }}>
                    Sem respostas registradas.
                  </div>
                )}

                {osOriginal.checklistUltimaExecucao.observacoes ? (
                  <div style={{ marginTop: 10, fontSize: 13, opacity: 0.9 }}>
                    <strong>Observações:</strong> {osOriginal.checklistUltimaExecucao.observacoes}
                  </div>
                ) : null}
              </div>
            ) : null}

{/* Materiais usados (Estoque Interno) */}
<div
  style={{
    marginTop: 14,
    padding: 12,
    borderRadius: 14,
    border: "1px solid var(--cor-borda-input, #334155)",
    background: "rgba(15,23,42,0.35)",
  }}
>
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
    <div style={{ fontWeight: 800 }}>
      Materiais usados (Estoque Interno)
    </div>
    {carregandoMateriaisOs && (
      <div style={{ fontSize: 12, opacity: 0.75 }}>Carregando...</div>
    )}
  </div>

  {(!carregandoMateriaisOs && materiaisAgrupados.length === 0) ? (
    <div style={{ marginTop: 8, fontSize: 13, opacity: 0.75 }}>
      Nenhum material registrado nesta OS.
    </div>
  ) : (
    <>
      <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
        {materiaisAgrupados.map((m) => (
          <div
            key={`${m.produto}|${m.codigo}|${m.local}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              padding: 10,
              borderRadius: 12,
              border: "1px solid rgba(148,163,184,0.18)",
              background: "rgba(15,23,42,0.25)",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {m.produto}
                {m.codigo ? ` (${m.codigo})` : ""}
              </div>
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                {m.local ? `Local: ${m.local} • ` : ""}
                Qtd: <strong>{m.quantidade}</strong>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
        Total de itens: <strong>{totalMateriais}</strong>
      </div>
    </>
  )}
</div>
            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={() => navigate("/gerenciar/tabela-os")}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="pc-btn-primario"
                disabled={
                  salvando ||
                  carregandoEmpresas ||
                  carregandoSetores ||
                  carregandoMaquinas
                }
              >
                {salvando ? "Salvando..." : "Salvar Alterações"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default EditarOS;
